import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Loader2, Bug, Pill, Search, ChevronDown, ChevronRight,
  Pencil, Save, Trash2, UserPlus, AlertTriangle, CheckCircle2, RefreshCw,
} from "lucide-react";
import { api, fmtDate, fmtDateTime } from "../lib/api";
import useEscClose from "../hooks/useEscClose";
import { confirmDialog } from "../utils/dialogs";

const MED_LABELS = {
  albendazole: "Albendazole",
  mebendazole: "Mebendazole",
  ivermectin: "Ivermectin",
  pyrantel: "Pyrantel pamoate",
  praziquantel: "Praziquantel",
  other: "Other",
};

/**
 * BulkBatchesModal — the "Recent bulk batches" panel.
 *
 * Lists every deworming + supplement bulk batch, sorted newest-first.
 * Expand a batch → edit its date, medication/name, dose in one shot
 * (applies to every athlete in the batch), plus remove or add athletes
 * without leaving the panel.
 *
 * `focusBatchId` — optional prop; if provided, that batch auto-expands
 * on open (used when a wizard detects an existing batch for the same
 * date and offers "edit existing" instead of "add fresh").
 */
export default function BulkBatchesModal({ open, onClose, focusBatchId }) {
  const [batches, setBatches] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [kindFilter, setKindFilter] = useState("all"); // all | deworming | supplement
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  useEscClose(open, onClose);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get("/bulk-batches");
      setBatches(r.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load batches");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    setKindFilter("all"); setSearch(""); setExpanded(new Set());
    load();
  }, [open]);

  // When focusBatchId is supplied (e.g. from BulkDewormingModal warning),
  // expand it as soon as data lands.
  useEffect(() => {
    if (open && focusBatchId && batches && !expanded.has(focusBatchId)) {
      setExpanded((s) => new Set([...s, focusBatchId]));
    }
  }, [open, focusBatchId, batches, expanded]);

  const filtered = useMemo(() => {
    if (!batches) return [];
    const q = search.trim().toLowerCase();
    return batches.filter((b) => {
      if (kindFilter !== "all" && b.kind !== kindFilter) return false;
      if (!q) return true;
      const label = b.kind === "deworming"
        ? `${MED_LABELS[b.data?.medication] || b.data?.medication || ""} ${b.data?.medication_other || ""}`
        : (b.data?.name || "");
      return label.toLowerCase().includes(q) || b.date?.includes(q);
    });
  }, [batches, kindFilter, search]);

  const toggle = (bid) => setExpanded((s) => {
    const n = new Set(s); n.has(bid) ? n.delete(bid) : n.add(bid); return n;
  });

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[10000] bg-[#040D1A]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-6 px-4"
      data-testid="bulk-batches-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="card w-full max-w-5xl p-5 md:p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
              <RefreshCw size={11} /> Bulk batches
            </div>
            <div className="font-display text-lg text-slate-100 mt-0.5">Recent bulk batches — deworming &amp; supplements</div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              Edit dates, medication/supplement names, add or remove athletes — changes cascade to every record in the batch.
            </div>
          </div>
          <button onClick={onClose} className="pill !p-1.5" data-testid="bulk-batches-close"><X size={14} /></button>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-200 px-3 py-2 text-[12px] flex items-start gap-2">
            <AlertTriangle size={12} className="mt-0.5" />{error}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="inline-flex rounded-lg border border-[#1E304B] overflow-hidden text-[11px]">
            {[
              { v: "all", l: "All", i: null },
              { v: "deworming", l: "Deworming", i: <Bug size={11} /> },
              { v: "supplement", l: "Supplements", i: <Pill size={11} /> },
            ].map((k) => (
              <button key={k.v} onClick={() => setKindFilter(k.v)}
                      className={`px-3 py-1.5 flex items-center gap-1 ${kindFilter === k.v ? "bg-cyan-500/20 text-cyan-200" : "text-slate-400 hover:text-slate-200"}`}
                      data-testid={`bulk-batches-filter-${k.v}`}>
                {k.i}{k.l}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" placeholder="Search medication or date (YYYY-MM-DD)…"
                   value={search} onChange={(e) => setSearch(e.target.value)}
                   className="pl-7 pr-3 py-1.5 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-sm placeholder-slate-500 focus:outline-none focus:border-[#00E5FF]/60 w-72"
                   data-testid="bulk-batches-search" />
          </div>
          <button onClick={load} className="pill !text-[11px] !py-1 !px-2.5 ml-auto" data-testid="bulk-batches-refresh">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>

        {/* List */}
        <div className="rounded-lg border border-[#1E304B] bg-[#0B1627] max-h-[65vh] overflow-y-auto" data-testid="bulk-batches-list">
          {loading ? (
            <div className="p-8 text-center text-slate-500 text-[12px]">
              <Loader2 size={14} className="animate-spin inline mr-2" /> Loading batches…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-[12px]">
              {batches && batches.length === 0
                ? "No bulk batches yet. Use \"Bulk deworming\" or \"Bulk supplements\" from an athlete card to create one."
                : "No batches match this filter."}
            </div>
          ) : (
            <div className="divide-y divide-[#1E304B]">
              {filtered.map((b) => (
                <BatchRow key={b.batch_id} batch={b}
                          expanded={expanded.has(b.batch_id)}
                          onToggle={() => toggle(b.batch_id)}
                          onChanged={load} />
              ))}
            </div>
          )}
        </div>

        <div className="mt-3 text-[10px] text-slate-500 italic">
          Tip: If a coach realises the wrong date was set, expand the batch and change it once — every athlete&apos;s record updates in one call.
        </div>
      </div>
    </div>,
    document.body,
  );
}

function KindBadge({ isDeworm }) {
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono-data border ${
      isDeworm ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-200"
               : "bg-cyan-500/15 border-cyan-500/40 text-cyan-200"
    }`}>
      {isDeworm ? <Bug size={9} /> : <Pill size={9} />}
      {isDeworm ? "Deworm" : "Supplement"}
    </span>
  );
}

function BatchRow({ batch, expanded, onToggle, onChanged }) {
  const isDeworm = batch.kind === "deworming";
  const label = isDeworm
    ? (batch.data?.medication === "other"
        ? (batch.data?.medication_other || "Other")
        : (MED_LABELS[batch.data?.medication] || batch.data?.medication || "—"))
    : (batch.data?.name || "—");

  const [editing, setEditing] = useState(false);
  const [draftDate, setDraftDate] = useState(batch.date);
  const [draftMed, setDraftMed] = useState(batch.data?.medication || "albendazole");
  const [draftMedOther, setDraftMedOther] = useState(batch.data?.medication_other || "");
  const [draftName, setDraftName] = useState(batch.data?.name || "");
  const [draftDose, setDraftDose] = useState(batch.data?.dose || "");
  const [busy, setBusy] = useState(false);
  const [rowError, setRowError] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    // If the underlying batch changes (parent reloaded), reset drafts.
    setDraftDate(batch.date);
    setDraftMed(batch.data?.medication || "albendazole");
    setDraftMedOther(batch.data?.medication_other || "");
    setDraftName(batch.data?.name || "");
    setDraftDose(batch.data?.dose || "");
  }, [batch]);

  const savePatch = async () => {
    setBusy(true); setRowError("");
    try {
      const payload = { date: draftDate, dose: draftDose || null };
      if (isDeworm) {
        payload.medication = draftMed;
        payload.medication_other = draftMed === "other" ? draftMedOther : null;
      } else {
        payload.name = draftName;
      }
      await api.patch(`/bulk-batches/${batch.batch_id}`, payload);
      setEditing(false);
      await onChanged?.();
    } catch (e) {
      setRowError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally { setBusy(false); }
  };

  const removeAthlete = async (sid, name) => {
    if (!(await confirmDialog(`Remove ${name} from this batch? Their record for ${fmtDate(batch.date)} will be deleted.`, { confirmLabel: "Remove", danger: true }))) return;
    setBusy(true); setRowError("");
    try {
      await api.delete(`/bulk-batches/${batch.batch_id}/athletes/${sid}`);
      await onChanged?.();
    } catch (e) {
      setRowError(e?.response?.data?.detail || e?.message || "Remove failed");
    } finally { setBusy(false); }
  };

  const deleteBatch = async () => {
    if (!(await confirmDialog(`Delete the entire batch (${batch.athlete_count} athlete${batch.athlete_count === 1 ? "" : "s"})? This can't be undone.`, { confirmLabel: "Delete", danger: true }))) return;
    setBusy(true); setRowError("");
    try {
      await api.delete(`/bulk-batches/${batch.batch_id}`);
      await onChanged?.();
    } catch (e) {
      setRowError(e?.response?.data?.detail || e?.message || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`bulk-batch-${batch.batch_id}`}>
      {/* Summary row */}
      <button onClick={onToggle}
              className="w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-[#0E1A2E]"
              data-testid={`bulk-batch-toggle-${batch.batch_id}`}>
        {expanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        <KindBadge isDeworm={isDeworm} />
        <span className="text-sm text-slate-100 flex-1 truncate">{label}</span>
        <span className="text-[11px] text-slate-400 font-mono-data">{fmtDate(batch.date)}</span>
        <span className="text-[10px] text-slate-500 font-mono-data">· {batch.athlete_count} athlete{batch.athlete_count === 1 ? "" : "s"}</span>
        {batch.last_saved_by && (
          <span className="text-[10px] text-slate-600 font-mono-data hidden md:inline">
            by {typeof batch.last_saved_by === "string"
              ? batch.last_saved_by
              : (batch.last_saved_by?.name || batch.last_saved_by?.email || "unknown")} · {fmtDateTime(batch.last_saved_at)}
          </span>
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 bg-[#0E1A2E]/40 border-t border-[#1E304B]" data-testid={`bulk-batch-expanded-${batch.batch_id}`}>
          {rowError && <div className="mt-2 text-[11px] text-rose-300">{rowError}</div>}

          {/* Editable header block */}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2">
            <label className="block md:col-span-1">
              <div className="text-[9px] uppercase tracking-widest text-slate-400">Date</div>
              {editing ? (
                <>
                  <input type="date" value={draftDate} onChange={(e) => setDraftDate(e.target.value)}
                         className="form-input w-full font-mono-data" data-testid={`bulk-batch-date-input-${batch.batch_id}`} />
                  <div className="text-[9px] text-slate-500 mt-0.5 font-mono-data">
                    DD/MM/YYYY · <span className="text-cyan-300">{fmtDate(draftDate)}</span>
                  </div>
                </>
              ) : (
                <div className="font-mono-data text-slate-200 text-[13px] py-1">{fmtDate(batch.date)}</div>
              )}
            </label>
            {isDeworm ? (
              <>
                <label className="block md:col-span-1">
                  <div className="text-[9px] uppercase tracking-widest text-slate-400">Medication</div>
                  {editing ? (
                    <select value={draftMed} onChange={(e) => setDraftMed(e.target.value)}
                            className="form-input w-full" data-testid={`bulk-batch-med-select-${batch.batch_id}`}>
                      {Object.entries(MED_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  ) : (
                    <div className="text-slate-200 text-[13px] py-1">{label}</div>
                  )}
                </label>
                {editing && draftMed === "other" && (
                  <label className="block md:col-span-2">
                    <div className="text-[9px] uppercase tracking-widest text-slate-400">Medication name</div>
                    <input type="text" value={draftMedOther} onChange={(e) => setDraftMedOther(e.target.value)}
                           className="form-input w-full" data-testid={`bulk-batch-med-other-${batch.batch_id}`} />
                  </label>
                )}
              </>
            ) : (
              <label className="block md:col-span-2">
                <div className="text-[9px] uppercase tracking-widest text-slate-400">Supplement name</div>
                {editing ? (
                  <input type="text" value={draftName} onChange={(e) => setDraftName(e.target.value)}
                         className="form-input w-full" data-testid={`bulk-batch-name-input-${batch.batch_id}`} />
                ) : (
                  <div className="text-slate-200 text-[13px] py-1">{label}</div>
                )}
              </label>
            )}
            <label className="block md:col-span-1">
              <div className="text-[9px] uppercase tracking-widest text-slate-400">Dose</div>
              {editing ? (
                <input type="text" value={draftDose} onChange={(e) => setDraftDose(e.target.value)}
                       className="form-input w-full" data-testid={`bulk-batch-dose-input-${batch.batch_id}`} />
              ) : (
                <div className="font-mono-data text-slate-300 text-[13px] py-1">{batch.data?.dose || "—"}</div>
              )}
            </label>
          </div>

          {/* Action buttons */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {editing ? (
              <>
                <button onClick={savePatch} disabled={busy}
                        className="pill active !text-[11px] !py-1 !px-2.5"
                        data-testid={`bulk-batch-save-${batch.batch_id}`}>
                  {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save changes to all {batch.athlete_count}
                </button>
                <button onClick={() => setEditing(false)} className="pill !text-[11px] !py-1 !px-2.5"
                        data-testid={`bulk-batch-cancel-edit-${batch.batch_id}`}>Cancel</button>
              </>
            ) : (
              <>
                <button onClick={() => setEditing(true)} className="pill !text-[11px] !py-1 !px-2.5"
                        data-testid={`bulk-batch-edit-${batch.batch_id}`}>
                  <Pencil size={11} /> Edit batch
                </button>
                <button onClick={() => setShowAdd(true)} className="pill !text-[11px] !py-1 !px-2.5"
                        data-testid={`bulk-batch-add-athletes-${batch.batch_id}`}>
                  <UserPlus size={11} /> Add athletes
                </button>
                <button onClick={deleteBatch} disabled={busy}
                        className="pill !text-[11px] !py-1 !px-2.5 ml-auto"
                        style={{ color: "#FCA5A5", borderColor: "rgba(239,68,68,0.4)" }}
                        data-testid={`bulk-batch-delete-${batch.batch_id}`}>
                  <Trash2 size={11} /> Delete batch
                </button>
              </>
            )}
          </div>

          {/* Athlete list */}
          <div className="mt-3 rounded-lg border border-[#1E304B] bg-[#0B1627] max-h-64 overflow-y-auto">
            <div className="divide-y divide-[#1E304B]">
              {batch.athletes.map((a) => (
                <div key={a.record_id}
                     className="px-3 py-1.5 flex items-center gap-2 text-[12px]"
                     data-testid={`bulk-batch-athlete-${batch.batch_id}-${a.sailor_id}`}>
                  <span className="text-slate-200 flex-1 truncate">· {a.sailor_name}</span>
                  <span className="text-slate-500 font-mono-data text-[10px]">{fmtDate(a.date)}</span>
                  <button onClick={() => removeAthlete(a.sailor_id, a.sailor_name)}
                          disabled={busy}
                          className="pill !text-[10px] !py-0.5 !px-1.5"
                          style={{ color: "#FCA5A5", borderColor: "rgba(239,68,68,0.4)" }}
                          title="Remove from batch"
                          data-testid={`bulk-batch-remove-athlete-${batch.batch_id}-${a.sailor_id}`}>
                    <Trash2 size={10} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {showAdd && (
            <AddAthletesPicker batchId={batch.batch_id}
                               excludeIds={new Set(batch.athletes.map((a) => a.sailor_id))}
                               onClose={() => setShowAdd(false)}
                               onAdded={async () => { setShowAdd(false); await onChanged?.(); }} />
          )}
        </div>
      )}
    </div>
  );
}

function AddAthletesPicker({ batchId, excludeIds, onClose, onAdded }) {
  const [roster, setRoster] = useState(null);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/sailors")
      .then((r) => setRoster(r.data || []))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load roster"));
  }, []);

  const filtered = useMemo(() => {
    if (!roster) return [];
    const q = search.trim().toLowerCase();
    return roster
      .filter((s) => !excludeIds.has(s.id))
      .filter((s) => !q || s.name.toLowerCase().includes(q))
      .slice(0, 100);
  }, [roster, excludeIds, search]);

  const toggle = (sid) => setPicked((p) => {
    const n = new Set(p); n.has(sid) ? n.delete(sid) : n.add(sid); return n;
  });

  const submit = async () => {
    if (picked.size === 0) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/bulk-batches/${batchId}/athletes`, { sailor_ids: [...picked] });
      await onAdded?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Add failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 p-3 rounded-lg border border-cyan-500/40 bg-cyan-500/[0.06]"
         data-testid={`bulk-batch-add-picker-${batchId}`}>
      <div className="flex items-center gap-2 mb-2">
        <UserPlus size={12} className="text-cyan-300" />
        <span className="text-[12px] text-cyan-200 font-display">Add athletes to this batch</span>
        <button onClick={onClose} className="pill !p-1 ml-auto" title="Close picker"
                data-testid={`bulk-batch-add-picker-close-${batchId}`}>
          <X size={11} />
        </button>
      </div>
      {err && <div className="text-[11px] text-rose-300 mb-2">{err}</div>}
      <div className="relative mb-2">
        <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
        <input type="text" placeholder="Search athletes…" value={search}
               onChange={(e) => setSearch(e.target.value)}
               className="pl-7 pr-3 py-1.5 bg-[#0B1627] border border-[#1E304B] rounded-lg text-[12px] w-72 focus:outline-none focus:border-[#00E5FF]/60"
               data-testid={`bulk-batch-add-picker-search-${batchId}`} />
      </div>
      <div className="max-h-40 overflow-y-auto rounded border border-[#1E304B] bg-[#0B1627] divide-y divide-[#1E304B]">
        {!roster ? (
          <div className="p-3 text-center text-slate-500 text-[11px]"><Loader2 size={12} className="animate-spin inline mr-1" /> Loading roster…</div>
        ) : filtered.length === 0 ? (
          <div className="p-3 text-center text-slate-500 text-[11px]">No matching athletes.</div>
        ) : filtered.map((s) => (
          <label key={s.id} className={`px-3 py-1.5 flex items-center gap-2 text-[12px] cursor-pointer hover:bg-[#0E1A2E] ${picked.has(s.id) ? "bg-cyan-500/[0.08]" : ""}`}>
            <input type="checkbox" checked={picked.has(s.id)} onChange={() => toggle(s.id)}
                   className="accent-cyan-400 w-3.5 h-3.5"
                   data-testid={`bulk-batch-add-check-${batchId}-${s.id}`} />
            <span className="text-slate-200 flex-1 truncate">{s.name}</span>
            <span className="text-[10px] text-slate-500 font-mono-data">{s.group || "—"}</span>
          </label>
        ))}
      </div>
      <div className="mt-2 flex justify-end gap-2">
        <button onClick={onClose} className="pill !text-[11px] !py-1 !px-2.5"
                data-testid={`bulk-batch-add-picker-cancel-${batchId}`}>Cancel</button>
        <button onClick={submit} disabled={busy || picked.size === 0}
                className="pill active !text-[11px] !py-1 !px-2.5 disabled:opacity-40"
                data-testid={`bulk-batch-add-picker-submit-${batchId}`}>
          {busy ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />} Add {picked.size} to batch
        </button>
      </div>
    </div>
  );
}
